/**
 * @module Logo
 */

"use client";

import Image from "next/image";
import { useTranslation } from "react-i18next";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";

/**
 * Props for the Logo component
 * @public
 */
export interface LogoProp {
  /** Width of the logo in pixels */
  width?: number;
  /** Height of the logo in pixels */
  height?: number;
  /** Optional CSS class names */
  className?: string;
  /** Whether to show the title text */
  isWithTitle?: boolean;
  /** Whether to enable animation */
  isAnimated?: boolean;
  /** Whether to toggle animation state */
  toggleAnimation?: boolean;
}

/**
 * Logo component for the application
 * @remarks Displays the application logo with optional title and animation
 */
const Logo: React.FC<LogoProp> = ({
  width = 200,
  height = 75,
  className,
}) => {
  const { t } = useTranslation();
  const { currentSchema } = useStyleSchema();
  const logoConfig = currentSchema.logo.main;
  
  // Bestimme die Tailwind-Klassen basierend auf der logoConfig
  const positionClasses = logoConfig.position?.x === 'center' ? 'justify-center' : 
                          logoConfig.position?.x === 'right' ? 'justify-end' : 'justify-start';
  
  // Da Tailwind mit dynamischen Werten nicht gut funktioniert, 
  // verwenden wir einen vernünftigen Standardwert basierend auf der Konfiguration
  let paddingClass = 'p-0';
  if (logoConfig.position?.padding) {
    // Vereinfachte Logik für gängige Padding-Werte
    if (logoConfig.position.padding.includes('10px')) paddingClass = 'p-2.5';
    else if (logoConfig.position.padding.includes('12px')) paddingClass = 'p-3';
    else if (logoConfig.position.padding.includes('14px')) paddingClass = 'p-3.5';
    else if (logoConfig.position.padding.includes('15px')) paddingClass = 'p-4';
    else if (logoConfig.position.padding.includes('20px')) paddingClass = 'p-5';
    else paddingClass = 'p-0'; // Standardwert
  }
  
  return (
    <div className={`flex ${positionClasses} ${paddingClass} ${className || ''}`}>
      <Image
          // Verwende immer die konfigurierten oder die Standard-Dimensionen als Ausgangsbasis
          width={logoConfig.size?.width ? parseInt(logoConfig.size.width) : width}
          height={logoConfig.size?.height === 'auto' ? height : parseInt(logoConfig.size?.height || height.toString())}
          src={logoConfig.path}
          alt={t("navigation.home")}
          // Füge min-width und min-height hinzu, um SVG-0x0-Probleme zu verhindern
          className="max-w-full min-w-[1rem] min-h-[1rem]"
          style={{
            maxWidth: logoConfig.size?.width || 'auto',
            // Direktes Setzen der Dimensionen für zuverlässigeres SVG-Rendering
            width: logoConfig.size?.width || 'auto',
            height: logoConfig.size?.height || 'auto'
          }}
          // Diese Eigenschaft verhindert, dass Next.js versucht, das Bild neu zu dimensionieren,
          // was besonders bei SVGs hilfreich ist
          unoptimized={logoConfig.path.endsWith('.svg')}
        />
    </div>
  );
};

export default Logo;
